import type { Modifiers } from './doClick';
import type { DispatchFocusFn } from './focusElement';
import type { StepQuirks } from 'clickim/src/common/models/step';
import type { TextStep } from 'clickim/src/common/models/textStep';
import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';

declare function getLocatedElement(locatedElement: ILocatedElement | string): Element | null;
declare const dispatchFocus: DispatchFocusFn;

interface SetTextEventData {
    eventType: string;
    events: TextStep['events'];
    quirks?: StepQuirks;
    locatedElement: ILocatedElement;
    isRoot?: boolean;
    elementToFocusLocatedElement?: false | ILocatedElement;
}
type BaseEvent = SetTextEventData['events'][number];
interface Context extends Pick<SetTextEventData, 'events' | 'quirks' | 'eventType'> {
    eventIndex: number;
    element: Element;
    isContentEditable?: boolean;
    isNonTextableElemnet?: boolean;
    isFocusable?: boolean;
    isSelectable?(e: Event): boolean;
    noEventExecuter?(_context: Context, userEvent: BaseEvent): void;
}

export const setText = function (eventData: SetTextEventData, done: (data: { status: 'done' | 'failed'; result?: string; success: boolean; reason?: string }) => void) {
    const eventConstructorSupported = typeof Event === 'function';
    const element = eventData.isRoot ? document.documentElement : getLocatedElement(eventData.locatedElement)!;
    const context: Context = {
        eventIndex: 0,
        element,
        events: eventData.events,
        eventType: eventData.eventType,
        quirks: eventData.quirks,
    };

    if (!element) {
        throw new Error('element not found');
    }

    window.__unloadNavigator = function () {
        resolve();
    };

    window.addEventListener('unload', window.__unloadNavigator);

    const keyboardEventNames = new Set(['keydown', 'keyup', 'keypress']);
    const MAX_KEYBOARD_EVENT_TIMEOUT = 15; //max msec between keyboard events

    function getTextInputEventObject(userEvent: BaseEvent) {
        try {
            if (!eventConstructorSupported) {
                return undefined;
            }

            const event = new CustomEvent('textInput', {
                bubbles: true,
                cancelable: true,
            });
            if (userEvent.eventData) {
                // @ts-expect-error both objects don't have a data property type
                event.data = userEvent.eventData.data;
            }
            return event;
        } catch (err) { /* ignored */ }
        return undefined;
    }

    function initTextInputEvent(userEvent: BaseEvent) {
        try {
            const event = document.createEvent('TextEvent');
            // @ts-expect-error both objects don't have a data property type
            event.data = userEvent.eventData.data;
            const method = 1; // keyboard
            // @ts-expect-error untyped?
            const locale = userEvent.eventData.locale || 'en-US';
            // @ts-expect-error untyped?
            event.initTextEvent('textInput', true, true, window, userEvent.eventData.data, method, locale);
            return event;
        } catch (err) { /* ignored */ }
        return undefined;
    }

    function getTextInputEvent(userEvent: BaseEvent) {
        return getTextInputEventObject(userEvent) || initTextInputEvent(userEvent);
    }

    function createKeyboardEventObject(eventType: string, _eventData: BaseEvent['eventData'], modifiers: Partial<Modifiers>) {
        try {
            return new KeyboardEvent(eventType, {
                bubbles: true,
                cancelable: true,
                // @ts-expect-error we never pass these properties
                location: _eventData.location || 0,
                // @ts-expect-error we never pass these properties
                key: _eventData.key || '',
                ctrlKey: Boolean(modifiers.ctrl),
                shiftKey: Boolean(modifiers.shift),
                altKey: Boolean(modifiers.alt),
                metaKey: Boolean(modifiers.meta),
            });
        } catch (err) { /* ignored */ }
        return undefined;
    }

    function initKeyboardEvent(eventType: string, _eventData: BaseEvent['eventData'], modifiers: Partial<Modifiers>) {
        try {
            const event = document.createEvent('KeyboardEvent');
            // @ts-expect-error this function was removed from all modern browsers
            event.initKeyEvent(
                eventType, // typeArg,
                true, // canBubbleArg,
                true, // cancelableArg,
                null, // viewArg,  Specifies UIEvent.view. This value may be null.
                Boolean(modifiers.ctrl),
                Boolean(modifiers.alt),
                Boolean(modifiers.shift),
                Boolean(modifiers.meta),
                // @ts-expect-error there is no key called key
                _eventData.key || '',
                0, // charCodeArg
            );

            return event;
        } catch (err) { /* ignored */ }
        return undefined;
    }

    function createKeyboardEvents(eventType: string, _eventData: BaseEvent['eventData'], modifiers: Partial<Modifiers>) {
        try {
            const event = document.createEvent('Events');
            event.initEvent(eventType, true, true);
            Object.assign(event, {
                altKey: Boolean(modifiers.alt),
                ctrlKey: Boolean(modifiers.ctrl),
                metaKey: Boolean(modifiers.meta),
                shiftKey: Boolean(modifiers.shift),
                // @ts-expect-error we never pass this property
                keyCode: _eventData.key || '',
            });
            return event;
        } catch (err) { /* ignored */ }
        return undefined;
    }

    function createKeyboardEvent(eventType: string, _eventData: BaseEvent['eventData'], modifiers: Partial<Modifiers>) {
        return createKeyboardEventObject(eventType, _eventData, modifiers)! ||
            initKeyboardEvent(eventType, _eventData, modifiers) ||
            createKeyboardEvents(eventType, _eventData, modifiers);
    }

    function getKeyboardEvent(userEvent: BaseEvent) {
        if (!keyboardEventNames.has(userEvent.event)) {
            return null;
        }

        const _eventData = userEvent.eventData;
        // @ts-expect-error we never pass modifiers, this is possibly bad copy-pasting
        const modifiers = _eventData.modifiers || {};

        const event = createKeyboardEvent(userEvent.event, _eventData, modifiers);

        // workaround for chromium bugs which make keycode etc readonly
        Object.defineProperties(event, {
            keyCode: {
                enumerable: true,
                get() {
                    return this._keyCode_;
                },
            },
            charCode: {
                enumerable: true,
                get() {
                    return this._charCode_;
                },
            },
            which: {
                enumerable: true,
                get() {
                    return this._keyCode_;
                },
            },
        });
        Object.assign(event, {
            // @ts-expect-error event might be badly typed
            _keyCode_: _eventData.keyCode,
            _charCode_: _eventData.charCode || 0,
        });
        return event;
    }

    function getEvent(userEvent: BaseEvent) {
        return userEvent.event === 'textInput' ?
            getTextInputEvent(userEvent) :
            getKeyboardEvent(userEvent);
    }

    function shouldSkipEvent(currentEvent: BaseEvent, _context: Context) {
        const isTextInputAuth0Form = () => currentEvent.event === 'textInput' && !_context.quirks?.isAuth0Form;
        return isTextInputAuth0Form();
    }

    function getTextEventTimeout(currentEvent: BaseEvent, nextEvent: BaseEvent) {
        return ((currentEvent.event === 'keyup') && (nextEvent.event === 'keydown')) ?
            MAX_KEYBOARD_EVENT_TIMEOUT :
            0;
    }

    function getTimeBetweenFiringEvents(currentEvent: BaseEvent, nextEvent: BaseEvent) {
        const maxTimeout = getTextEventTimeout(currentEvent, nextEvent);
        return Math.min(nextEvent.timeStamp! - currentEvent.timeStamp!, maxTimeout);
    }

    function handleReactFormFocus(_element: HTMLInputElement | HTMLTextAreaElement) {
        // solution taken partially from https://github.com/vitalyq/react-trigger-change
        const descriptor = Object.getOwnPropertyDescriptor(_element, 'value');
        if (!descriptor) {
            return;
        }
        const initialValue = _element.value;
        _element.value = `${initialValue}#`;
        if (descriptor.configurable) {
            // @ts-expect-error force remove the value
            delete _element.value;
        }
        _element.value = initialValue;
        const event = document.createEvent('HTMLEvents');
        event.initEvent('input', true, false);
        _element.dispatchEvent(event);
        Object.defineProperty(_element, 'value', descriptor);
    }

    function handleChangeEventIfNeeded(_context: Context) {
        if (!isFocusableInput(_context.element)) {
            return;
        }
        try {
            handleReactFormFocus(_context.element);
            if (eventConstructorSupported) {
                _context.element.dispatchEvent(new Event('change'));
            } else {
                const event = document.createEvent('HTMLEvents');
                event.initEvent('change', false, true);
                _context.element.dispatchEvent(event);
            }
        } catch (e) { /* ignored */ }
    }

    function nextAsyncEvent(_context: Context, currentEvent: BaseEvent, nextEvent: BaseEvent) {
        if (nextEvent) {
            setTimeout(() => {
                executeAsyncNext(_context);
            }, getTimeBetweenFiringEvents(currentEvent, nextEvent));
        } else {
            if (window.__unloadNavigator) {
                window.removeEventListener('unload', window.__unloadNavigator);
            }
            handleChangeEventIfNeeded(_context);
            resolve();
        }
    }

    function shouldDispatchOnParentElement(eventType: string, _context: Context) {
        return (eventType === 'change' || eventType === 'blur') && _context.element.tagName === 'OPTION';
    }

    function getElement(event: Event, _context: Context, currentEvent: BaseEvent) {
        if (shouldDispatchOnParentElement(event.type, _context)) {
            return _context.element.parentElement;
            // @ts-expect-error untyped
        } if (currentEvent.locatedElement) {
            // @ts-expect-error untyped
            return getLocatedElement(currentEvent.locatedElement);
        }
        return _context.element;
    }

    function findTextNode(_element: Node, offsetData: { offset: number }): ChildNode | null {
        let child = _element.firstChild;
        let innerChild;
        while (child) {
            if (child.nodeType === 3) {
                if (offsetData.offset-- <= 0) {
                    return child;
                }
            } else if (child.nodeType === 1) {
                innerChild = findTextNode(child, offsetData);
                if (innerChild) {
                    return innerChild;
                }
            }
            child = child.nextSibling;
        }
        return null;
    }

    function setSelection(_element?: Element, selection?: { start: number; end: number; nodeOffset: number; textOffset: number }) {
        if (!_element || !selection) {
            return;
        }
        if (!isNaN(selection.start)) {
            Object.assign(_element, {
                selectionStart: selection.start,
                selectionEnd: selection.end,
            });
        } else if (!isNaN(selection.nodeOffset)) {
            let startNode;
            if (_element.firstChild) {
                startNode = findTextNode(_element, { offset: selection.nodeOffset });
            } else {
                startNode = _element;
            }
            if (startNode) {
                const sel = window.getSelection()!;
                const range = document.createRange();

                try { // until we get our act together regarding contenteditable, this may throw when the numbers in the recorded data don't match the current text
                    sel.removeAllRanges();
                    range.setStart(startNode, selection.textOffset);
                    range.setEnd(startNode, selection.textOffset);
                    sel.addRange(range);
                } catch (ignore) { /* ignored */ }
            }
        }
    }

    function isSubmitFormAction(eventType: string, _element: Element): _element is HTMLFormElement {
        return eventType === 'submit' && Boolean((_element as HTMLFormElement).action);
    }

    function executeAsyncEvent(event: Event, _context: Context, currentEvent: BaseEvent) {
        if (_context.isFocusable && _context.isSelectable!(event) && event.type !== 'submit') {
            try {
                // @ts-expect-error selection is untyped
                setSelection(_context.element, currentEvent.eventData.selection);
            } catch (ignore) { /* ignored */ }
        }
        const _element = getElement(event, _context, currentEvent);
        if (!_element) {
            throw new Error('could not find element');
        }
        if (isSubmitFormAction(event.type, _element)) {
            _element.submit();
        } else {
            _element.dispatchEvent(event);
        }
    }

    function executeAsyncNext(_context: Context) {
        let event;
        const currentEvent = _context.events[_context.eventIndex];
        const nextEvent = _context.events[++_context.eventIndex];
        try {
            event = getEvent(currentEvent);
        } catch (err) {
            return reject(`exception in get event in text step:${err.message}`);
        }

        if (shouldSkipEvent(currentEvent, _context)) {
            return nextAsyncEvent(_context, currentEvent, nextEvent);
        } if (event) {
            try {
                executeAsyncEvent(event, _context, currentEvent);
            } catch (err) {
                return reject(`exception in executeEvent in text step:${err.message}`);
            }
        } else if (_context.noEventExecuter) {
            _context.noEventExecuter(_context, currentEvent);
        } else {
            return reject(`cannot execute event ${currentEvent.event}`);
        }
        nextAsyncEvent(_context, currentEvent, nextEvent);
        return undefined;
    }

    function isFocusableInput(target: Element): target is HTMLInputElement | HTMLTextAreaElement {
        const tagName = target.tagName;
        return (tagName === 'INPUT' || tagName === 'TEXTAREA');
    }

    function isContentEditable(target: Element) {
        return target.getAttribute ?
            Boolean(target.getAttribute('contenteditable') === 'true') :
            false;
    }

    function executeSetValue(_context: Context, userEvent: BaseEvent) {
        if (isFocusableInput(_context.element)) {
            _context.element.value = userEvent.eventData.text;
            const inputEvent = document.createEvent('Event');
            inputEvent.initEvent('input', true, false);
            _context.element.dispatchEvent(inputEvent);
        } else if (_context.isContentEditable) {
            _context.element.innerHTML = userEvent.eventData.text;
        }
    }

    function findEffectiveActiveElement() {
        let activeElement = document.activeElement!;
        while (activeElement.shadowRoot?.activeElement) {
            activeElement = activeElement.shadowRoot.activeElement;
        }
        return activeElement;
    }

    function resolve(result?: never) {
        const status = {
            status: 'done' as const,
            result,
            success: true,
            ...(context.isNonTextableElemnet && { reason: 'Set text on non input element' }),
        };

        done(status);
    }

    function reject(result?: string) {
        result ||= '';
        const status = {
            status: 'failed' as const,
            result,
            success: false,
            ...(context.isNonTextableElemnet && { reason: 'Set text on non input element' }),
        };

        done(status);
    }

    try {
        const isInput = isFocusableInput(context.element);
        context.isContentEditable = isContentEditable(context.element);
        if (!isInput && !context.isContentEditable) {
            context.isNonTextableElemnet = true;
        }
        context.isFocusable = isInput || context.isContentEditable;
        context.isSelectable = event => event.type !== 'keyup';
        context.noEventExecuter = executeSetValue;
    } catch (err) {
        reject(`exception in set text step:${err.message}`);
        return;
    }

    const oldActiveElement = findEffectiveActiveElement();
    dispatchFocus(eventData.elementToFocusLocatedElement, oldActiveElement);
    executeAsyncNext(context);
};
